import { Container, Graphics, Text } from 'pixi.js';

import { BaseWidget } from 'ff/debrief/components/hud/widgets/BaseWidget';
import { CurrentPoint } from 'ff/debrief/models';
import { HudContext } from 'ff/debrief/components/hud/HudContext';
import { HudPositionTranslator } from 'ff/debrief/components/hud/utils';

/**
 * Airspeed tape widget that displays a scrolling vertical tape similar to the Garmin G5.
 * Shows major graduations every 20 knots and minor divisions every 10 knots.
 * The tape scrolls vertically as airspeed changes.
 */
export class AirspeedTapeWidget extends BaseWidget {
    static readonly requiredProperties = ['indicatedAirSpeed'] as const;
    static readonly optionalProperties = [] as const;

    private container: Container;
    private tapeGraphics: Graphics;
    private labelsContainer: Container;

    // Configuration constants
    private readonly PIXELS_PER_KNOT = 2.5; // Scale factor for tape movement
    private readonly MAJOR_INTERVAL = 20; // Major graduations every 20 knots
    private readonly MINOR_INTERVAL = 10; // Minor divisions every 10 knots
    private readonly TAPE_BUFFER = 100; // Draw this many knots above/below viewport
    private readonly MAJOR_TICK_WIDTH = 10;
    private readonly MINOR_TICK_WIDTH = 5;

    private currentAirspeed = 0;
    private lastDrawnAirspeed = 0;

    async populateElementContainer(
        elementContainer: Container,
        hudContext: HudContext,
        positionTranslator: HudPositionTranslator
    ): Promise<void> {
        this.container = elementContainer;

        // Create graphics object for the tape
        this.tapeGraphics = new Graphics();
        this.labelsContainer = new Container();

        // Create viewport mask to clip the tape to the visible area
        const viewportMask = new Graphics()
            .rect(
                positionTranslator.x(0),
                positionTranslator.y(0),
                positionTranslator.x(1),
                positionTranslator.y(1)
            )
            .fill(0xffffff);
        elementContainer.addChild(viewportMask);
        elementContainer.mask = viewportMask;

        // Add tape and labels to container
        elementContainer.addChild(this.tapeGraphics);
        elementContainer.addChild(this.labelsContainer);
    }

    protected updateWidget(
        point: CurrentPoint,
        hudContext: HudContext,
        positionTranslator: HudPositionTranslator
    ): void {
        this.currentAirspeed = point.indicatedAirSpeed;

        // Redraw the tape if airspeed has changed significantly or if this is the first draw
        if (
            this.lastDrawnAirspeed === 0 ||
            Math.abs(this.currentAirspeed - this.lastDrawnAirspeed) > 50
        ) {
            this.drawTape(hudContext, positionTranslator);
            this.lastDrawnAirspeed = this.currentAirspeed;
        }

        // Update tape position to create scrolling effect
        this.updateTapePosition();
    }

    /**
     * Draws the airspeed tape with major and minor graduations.
     * Draws a range centered on current airspeed to provide buffer for scrolling.
     */
    private drawTape(
        hudContext: HudContext,
        positionTranslator: HudPositionTranslator
    ): void {
        // Clear existing graphics
        this.tapeGraphics.clear();
        this.labelsContainer.removeChildren();

        const color = 'white';
        const centerY = positionTranslator.y(0.5);

        // Calculate airspeed range to draw (don't go below 0)
        const minAirspeed = Math.max(
            0,
            Math.floor(
                (this.currentAirspeed - this.TAPE_BUFFER) / this.MINOR_INTERVAL
            ) * this.MINOR_INTERVAL
        );
        const maxAirspeed =
            Math.ceil(
                (this.currentAirspeed + this.TAPE_BUFFER) / this.MINOR_INTERVAL
            ) * this.MINOR_INTERVAL;

        // Spine on far right side, tick marks and labels extend to the left
        const spineX = positionTranslator.x(1);

        // Draw all graduations
        for (
            let airspeed = minAirspeed;
            airspeed <= maxAirspeed;
            airspeed += this.MINOR_INTERVAL
        ) {
            const isMajor = airspeed % this.MAJOR_INTERVAL === 0;
            const tickWidth = isMajor
                ? this.MAJOR_TICK_WIDTH
                : this.MINOR_TICK_WIDTH;

            // Calculate Y position relative to center
            const airspeedDiff = airspeed - this.currentAirspeed;
            const y = centerY - airspeedDiff * this.PIXELS_PER_KNOT;

            // Add tick mark to path - extend to the LEFT
            this.tapeGraphics.moveTo(spineX, y);
            this.tapeGraphics.lineTo(spineX - tickWidth, y);

            // Draw label for major graduations - position to the LEFT of tick
            if (isMajor) {
                // Airspeed already converted by HudRenderer
                const label = new Text({
                    text: Math.round(airspeed).toString(),
                    style: {
                        fontFamily: hudContext.globalConfig.fontFamily,
                        fontSize: hudContext.globalConfig.baseFontSize + 2,
                        fill: color,
                    },
                });
                label.anchor.set(1, 0.5); // Anchor right, center vertically
                label.position.set(spineX - tickWidth - 5, y);
                this.labelsContainer.addChild(label);
            }
        }

        // Stroke all the lines at once
        this.tapeGraphics.stroke({ width: 2, color });
    }

    /**
     * Updates the position of the tape graphics to create scrolling effect.
     * The tape moves down as airspeed increases and up as airspeed decreases.
     */
    private updateTapePosition(): void {
        const airspeedDiff = this.currentAirspeed - this.lastDrawnAirspeed;
        const offset = airspeedDiff * this.PIXELS_PER_KNOT;

        // Move both the graphics and labels together
        // Positive offset (accelerating) moves tape down, negative (decelerating) moves up
        this.tapeGraphics.position.y = offset;
        this.labelsContainer.position.y = offset;
    }

    cleanUp(): void {
        this.tapeGraphics?.destroy();
        this.labelsContainer?.destroy();
        this.container?.destroy();

        this.tapeGraphics = null;
        this.labelsContainer = null;
        this.container = null;
    }
}
